
import { HttpClient, HttpParams, HttpHeaders, HttpRequest, HttpEvent } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError, from } from 'rxjs';
import { Role, User, UserAudit, UserName, UsersDetail, mailDTO } from '../shared/models/user.model'
import { AddEditSolarForm, AddSupplier, AdditionalSettingsFileExtension, HomePageContents, IAMUser, sectionDetails, supplierById, supplierDetails } from '../shared/models/admin.model';
import { environment } from 'src/environments/environment';
import { AuthService } from './auth.service';
import { UserInfo } from '../shared/models/userinfo';
import 'rxjs/add/observable/of';
import { FolderStructure, SolarForms, SupplierDetails, UploadFile, DocumentsTypeByPath, DocumentsDetailsRawData, GetMaterialMetaData, GetDocumentTypesRequest, IsFileExists, FileUploadPathByDocumentType, GetFilePathByDocumentType } from '../shared/models/documents.model';
import { WindchillAccessResponse } from '../shared/models/news-updates.model';
import { AutomatedForms, CWSForm, RTADetails, RemoveRTAFiles,CWSRequest } from '../shared/models/solarForms.model';
import { ExportDeliveryPerformace, ExportInvoiceInfo, ExportOpenOrder } from '../metrics/supplier-delivery-performance/metrics.model';
import { AddEditNotification, DeleteInfo, NotificationFilters} from '../shared/models/notification.model';
import { QualityData, SupplierQualitySummaryTable, forecastFilters, itemLeadTimeDetails } from '../shared/models/metrics.model';
import { MSALInstanceFactory } from '../sign-in/msal-factory';
import { QRCodeMetricsDTO } from '../metrics/metrics-model/metrics.model';
import { DownloadQRImage } from '../shared/models/branding-qr.model';

let API_PREFIX = 'api';

let QR_API_PREFIX = 'api';

/**
 * This exception is thrown by the DataSourceService on known conditions that the calling class usually wants to handle by displaying
 * a customized error message to the user.
 * Do not throw this exception in generic situations (e.g. network error) or in situations that are result of a programming error
 * (for example HTTP 500 or back-end rejecting the API call because of missing or faulty front-end validation).
 */
export class ApiException {
  userFriendlyMessage: string;

  constructor(userFriendlyMessage: string) {
    this.userFriendlyMessage = userFriendlyMessage;
  }
}

@Injectable({
  providedIn: 'root'
})
export class DataSourceService {

  constructor(private http: HttpClient, private authService: AuthService) {
    //Could be a better way to handle this. Only applicable for localhost. Change port number accordingly as per your environment needs.
    API_PREFIX = environment.ApiUrl;
    QR_API_PREFIX = environment.QRApiUrl;
    // if(environment.envt == "localhost")
    // {
    //   API_PREFIX="http://localhost:8082/api";
    // }
    // else
    // {
    //   API_PREFIX=environment
    // }
  }

  // --------------------- Users API ----------------------

  getMe(): Observable<User> {
    return this.http.get<User>(`${API_PREFIX}/User/GetCurrentUser`);
  }

  getAccess(businessJustification: string): Observable<any> {
    let params = new HttpParams().set("businessJustification", businessJustification)
    return this.http.get<any>(`${API_PREFIX}/User/RequestAccess`, { params: params });
  }

  getUserNames(): Observable<UserName[]> {
    return this.http.get<UserName[]>(`${API_PREFIX}/users/names`);
  }

  getAllUsers(): Observable<UsersDetail[]> {
    return this.http.get<UsersDetail[]>(`${API_PREFIX}/Admin/GetAllUsers`);
  }

  getUserById(id: string): Observable<UserInfo> {
    return this.http.get<UserInfo>(`${API_PREFIX}/Admin/GetUserById?userId=${id}`);
  }

  getAllRoles(): Observable<Role[]> {
    return this.http.get<Role[]>(`${API_PREFIX}/Admin/GetAllRoles`);
  }

  updateUser(userData: User): Observable<User> {
    return this.http.post<User>(`${API_PREFIX}/Admin/UpdateUser`, userData);
  }

  createUser(userData: User): Observable<any> {
    return this.http.post<User>(`${API_PREFIX}/Admin/AddUser`, userData);
  }

  searchIAMWithName(name: string): Observable<IAMUser> {
    return this.http.get<IAMUser>(`${API_PREFIX}/Admin/SearchUser?name=${name}`);
  }

  getHomePageContents(): Observable<any> {
    return this.http.get<sectionDetails>(`${API_PREFIX}/StaticContent/GetHomepageStaticContents`);
  }

  postMassCommunication(massCommDetails: mailDTO): Observable<mailDTO> {
    return this.http.post<mailDTO>(`${API_PREFIX}/Admin/SendMassCommunication`, massCommDetails);
  }
  
  // --------------------- Add further methods as needed ----------------------

  getDocumentsFolder(supplierId: string): Observable<FolderStructure> {
    return this.http.get<FolderStructure>(`${API_PREFIX}/Metadata/GetFolderStructure?supplierId=${supplierId}`);
  }

  getSupplierDetailsById(id: string): Observable<SupplierDetails> {
    return this.http.get<SupplierDetails>(`${API_PREFIX}/Supplier/GetSupplierById?supplierId=${id}`);
  }
  getAllSuppliers(): Observable<SupplierDetails[]> {
    return this.http.get<SupplierDetails[]>(`${API_PREFIX}/Supplier/GetSuppliers`);
  }
  getSolarForms(): Observable<SolarForms[]> {
    return this.http.get<SolarForms[]>(`${API_PREFIX}/Metadata/GetSolarForms`);
  }
  getSolarFormFileById(id: string): Observable<Blob> {
    return this.http.get(`${API_PREFIX}/Metadata/GetSolarFormFile?formId=${id}`, { responseType: 'blob' });
  }
  getDownloadAccountFormById(id: string): Observable<Blob> {
    return this.http.get(`${API_PREFIX}/CWSForms/DownloadAccountRequestForm?requestFormId=${id}`, { responseType: 'blob' });
  }
  uploadSolarForm(data: FormData): Observable<HttpEvent<any>> {
    const req = new HttpRequest('POST', `${API_PREFIX}/Files/UploadSolarForm`, data, { reportProgress: true });
    return this.http.request(req);
  }
  editUploadSolarForm(data: FormData, fileId: string): Observable<HttpEvent<any>> {
    const req = new HttpRequest('POST', `${API_PREFIX}/Files/EditSolarForm?fileId=${fileId}`, data, { reportProgress: true });
    return this.http.request(req);
  }
  getValidFileExtensions(): Observable<string[]> {
    return this.http.get<[string]>(`${API_PREFIX}/Metadata/GetValidFileExtensions`);
  }
  getUploadedFilesByPath(filePathData: GetDocumentTypesRequest): Observable<DocumentsDetailsRawData[]> {
    return this.http.post<DocumentsDetailsRawData[]>(`${API_PREFIX}/Files/GetFilesByPath`, filePathData);
  }
  getDocumentTypesByPath(filePathData: GetDocumentTypesRequest): Observable<DocumentsTypeByPath> {
    return this.http.post<DocumentsTypeByPath>(`${API_PREFIX}/Metadata/GetDocumentTypesByPath`, filePathData);
  }
  getMaterialMetaData(): Observable<GetMaterialMetaData[]> {
    return this.http.get<GetMaterialMetaData[]>(`${API_PREFIX}/Metadata/GetMaterialMetaData`);
  }
  getUploadedFilesById(fileId: string): Observable<DocumentsDetailsRawData> {
    return this.http.get<DocumentsDetailsRawData>(`${API_PREFIX}/Files/GetFileById?fileId=${fileId}`);
  }

  getFileDownloadById(id: string): Observable<Blob> {
    return this.http.get(`${API_PREFIX}/Files/DownloadFileById?fileId=${id}`, { responseType: 'blob' });
  }

  previewFileById(id: string): Observable<Blob> {
    return this.http.get(`${API_PREFIX}/Files/ViewDocumentByFileId?fileId=${id}`, { responseType: 'blob' });
  }

  getFilePreviewById(id: string): Observable<any> {
    return this.http.get(`${API_PREFIX}/Files/GetFilePathByFielId?fileId=${id}`,  { responseType: 'blob' });
  }

  isDocumentExit(fileName: string, supplierId: string): Observable<Boolean> {
    return this.http.get<boolean>(`${API_PREFIX}/Files/IsFileExists?fileName=${fileName}&supplierId=${supplierId}`);
  }
  getActiveProjectLink(cwsId: any): Observable<WindchillAccessResponse> {
    return this.http.get<WindchillAccessResponse>(`${API_PREFIX}/WindchillData/HasWindchillAccess?cwsid=${cwsId}`);
  }

  getUploadPathByDocumentType(data: GetFilePathByDocumentType): Observable<any> {
    const requestOptions: Object = {
      responseType: 'text'
    }
    return this.http.post(`${API_PREFIX}/Metadata/GenerateFileUploadPathByDocumentType`, data, requestOptions);
  }

  documentSearch(params: HttpParams) {
    return this.http.get(`${API_PREFIX}/Files/DocumentSearch`, { params });
  }
  getAutomatedFormRequests(id: string, cwsFormOption: number): Observable<AutomatedForms[]> {
    return this.http.get<AutomatedForms[]>(`${API_PREFIX}/CWSForms/AutomatedFormRequests?userId=${id}&viewMode=${cwsFormOption}`);
  }

  getAutomatedFormRequestById(requestId: string): Observable<CWSForm> {
    return this.http.get<CWSForm>(`${API_PREFIX}/CWSForms/GetAutomatedFormRequestById?requestId=${requestId}`);
  }

  // getAutomatedRequestFormByCwsNumber(requestId: string, token:string): Observable<CWSForm> {
  //   let headers = new HttpHeaders();
  //   headers = headers.append('externalLinkToken', `${token}`);
  //   return this.http.get<CWSForm>(`${API_PREFIX}/ExternalLink/GetAutomatedFormRequestByCWSRequestNumber?CWSRequestNumber=${requestId}`,{ headers: headers });
  // }
  getAutomatedRequestFormByCwsNumber(data: CWSRequest, token:string): Observable<CWSForm> {
    let headers = new HttpHeaders();
    headers = headers.append('externalLinkToken', `${token}`);
    return this.http.post<CWSForm>(`${API_PREFIX}/ExternalLink/GetAutomatedFormRequestByCWSRequestNumber`,data,{ headers: headers });
  }

  updateExternalFormData(data: CWSForm, token, expires): Observable<any> {
    let headers = new HttpHeaders();
    headers = headers.append('externalLinkToken', `${token}`);
    headers = headers.append('X-External-Token', `${token}`);
    headers = headers.append('X-External-Expires', `${expires}`);
    return this.http.post(`${API_PREFIX}/ExternalLink/CWSAccountUpdate`, data, { headers: headers });
  }

  postCwsFormData(data: CWSForm): Observable<any> {
    return this.http.post(`${API_PREFIX}/CWSForms/CreateCWSAccount`, data);
  }
  updateCwsFormData(data: CWSForm): Observable<any> {
    return this.http.post(`${API_PREFIX}/CWSForms/EditCWSAccount`, data);
  }

  resendCWSNotification(formId: string): Observable<any> {
    return this.http.post(`${API_PREFIX}/CWSForms/ResendNotification?formId=${formId}`, null);
  }

  softDeleteSolarForm(fileId: string) {
    return this.http.post(`${API_PREFIX}/Files/SoftDeleteFile?fileId=${fileId}`, fileId);
  }

  getUndoSolarFormRecord(fileId: string) {
    return this.http.post(`${API_PREFIX}/Files/UndoFile?fileId=${fileId}`, fileId);
  }

  /*Metrics*/
  getDeliveryPerformanceData(supplierId: string, params: HttpParams) {
    return this.http.get(`${API_PREFIX}/Metrics/DeliveryPerformanceData?supplierId=${supplierId}&${params}`);
  }

  getDeliveryPerformanceFilter(supplierId: string, params: HttpParams) {
    return this.http.get(`${API_PREFIX}/Metrics/GetDeliverypPerformanceFilters?supplierId=${supplierId}&${params}`);
  }

  getAllSupplierUserAssociatedWithBuyer(cwsIdNo: string, supplierId: string): Observable<any> {
    return this.http.get<SupplierDetails[]>(`${API_PREFIX}/User/GetAllSupplierUsersBySupplierId?cwsId=${cwsIdNo}&supplierId=${supplierId}`);
  }
  getAllAssociatedBuyersWithSupplier(cwsIdNo: string): Observable<any> {
    return this.http.get<SupplierDetails[]>(`${API_PREFIX}/User/GetAllAssociatedBuyersWithSupplier?cwsId=${cwsIdNo}`);
  }
  getBuyersBySupplierId(cwsId: string, supplierId: string): Observable<any> {
    return this.http.get<any[]>(`${API_PREFIX}/User/GetBuyersBySupplierId?cwsId=${cwsId}&supplierId=${supplierId}`);
  }
  getSuppliersByBuyerId(cwsId: string): Observable<SupplierDetails[]> {
    return this.http.get<SupplierDetails[]>(`${API_PREFIX}/Supplier/GetSuppliersByBuyerId?cwsId=${cwsId}`);
  }
  
  // getOpenOrderTableData(supplierId: string, params: HttpParams) {    
  //   return this.http.get(`${API_PREFIX}/Metrics/GetOpenOrderReportTableData?supplierId=${supplierId}&${params}`);
  // }

  GetOpenOrderReport(supplierId: string, params: HttpParams) {
    return this.http.get(`${API_PREFIX}/Metrics/GetOpenOrderReport?supplierId=${supplierId}&${params}`);
  }

  getOpenOrderFilter(supplierId: string) {
    return this.http.get(`${API_PREFIX}/Metrics/GetOpenOrderReportFilters?supplierId=${supplierId}`);
  }

  // getOpenOrderReport(supplierId: string) {
  //   return this.http.get(`${API_PREFIX}/Metrics/GetOpenOrderReport?supplierId=${supplierId}`);
  // }

  exportData(): Observable<Blob> {
    return this.http.get(`${API_PREFIX}/Admin/DownloadUserData`, { responseType: 'blob' });
  }

  exportSupplierData(filter: string): Observable<Blob>{
    return this.http.get(`${API_PREFIX}/Admin/DownloadSupplierData?filter=${filter}`, { responseType: 'blob' });
  }

  getInvoiceTableData(supplierId:string, params: HttpParams){
    return this.http.get(`${API_PREFIX}/Metrics/GetInvoiceDetailsTableData?supplierId=${supplierId}&${params}`);
  }
  
  getInvoiceDetailsFilters(supplierId: string, params: HttpParams) {
    return this.http.get(`${API_PREFIX}/Metrics/GetInvoiceDetailsFilters?supplierId=${supplierId}&${params}`);
  }

  deliveryPerformanceExportData(exportData: ExportDeliveryPerformace): Observable<any> {
    return this.http.post(`${API_PREFIX}/Metrics/DownloadDeliveryPerformanceData`, exportData, { responseType: 'blob' });
  }

  openOrderExportData(exportData: ExportOpenOrder): Observable<any> {
    return this.http.post(`${API_PREFIX}/Metrics/DownloadOpenOrderReport`, exportData, { responseType: 'blob' });
  }

  invoiceInfoExportData(exportData: ExportInvoiceInfo): Observable<any> {
    return this.http.post(`${API_PREFIX}/Metrics/DownloadInvoiceDetailsData`, exportData, { responseType: 'blob' });
  }

  leadItemDetails(supplierId: string, baanCompany?: string): Observable<itemLeadTimeDetails> {
    return this.http.get<itemLeadTimeDetails>(`${API_PREFIX}/Metrics/GetLeadTimeDetails?supplierId=${supplierId}&baanCompany=${baanCompany}`);
  }

  leadItemDetailsFilter(supplierId: string): Observable<itemLeadTimeDetails> {
    return this.http.get<itemLeadTimeDetails>(`${API_PREFIX}/Metrics/GetItemLeadTimeFilters?supplierId=${supplierId}`);
  }

  leadItemExportData(exportData): Observable<any> {
    return this.http.post(`${API_PREFIX}/Metrics/DownloadItemLeadTimeReport`, exportData, { responseType: 'blob' });
  }

  getForecastReport(forecastFilter: forecastFilters): Observable<forecastFilters> {
    return this.http.post<forecastFilters>(`${API_PREFIX}/Metrics/GetForecastDetails`, forecastFilter);
  }
  
  getForecastFilters(supplierId: string): Observable<any> {
    return this.http.get<any>(`${API_PREFIX}/Metrics/GetForecastReportFilters?supplierId=${supplierId}`);
  }

  downloadForecastReport(forecastFilter: forecastFilters): Observable<any> {
    return this.http.post(`${API_PREFIX}/Metrics/DownloadForecastReport`, forecastFilter, { responseType: 'blob' });
  }
  
  // --------------------- Supplier Management Methods Start ----------------------
  
  getSuppliersByFilter(FilterId: string): Observable<supplierDetails> {
    return this.http.get<supplierDetails>(`${API_PREFIX}/Admin/GetSuppliersByFilter?filter=${FilterId}`);
  }

  getSuppliersById(Id: string): Observable<supplierById> {
    return this.http.get<supplierById>(`${API_PREFIX}/Admin/GetSupplierById?supplierId=${Id}`);
  }

  searchSuppliersBySupplierCode(supplierCode: string): Observable<any> {
    return this.http.get<any>(`${API_PREFIX}/Admin/SearchSupplier?supplierCode=${supplierCode}`);
  }

  addSupplierInfo(supplierInfo: AddSupplier): Observable<any> {
    return this.http.post<AddSupplier>(`${API_PREFIX}/Admin/AddSupplier`, supplierInfo);
  }

  editSupplierInfo(supplierInfo: AddSupplier): Observable<any> {
    return this.http.post<AddSupplier>(`${API_PREFIX}/Admin/EditSupplier`, supplierInfo);
  }
  
  isSupplierExist(supplierCode: string): Observable<any> {
    return this.http.get<any>(`${API_PREFIX}/Admin/IsSupplierExists?supplierCode=${supplierCode}`);
  }

  getSupplyChainUsers(supplierCode?: string): Observable<any> {
    return this.http.get<any>(`${API_PREFIX}/Admin/GetSupplyChainUsers`);
  }

  getSuppliersWithBuyers(): Observable<any> {
    return this.http.get<any>(`${API_PREFIX}/Admin/GetSuppliersWithBuyers`);
  }

  addAdditionalBuyers(supplierInfo: any): Observable<any> {
    return this.http.post<any>(`${API_PREFIX}/Admin/AddAdditionalBuyers`, supplierInfo);
  }

  replaceAdditionalBuyers(supplierInfo: any): Observable<any> {
    return this.http.post<any>(`${API_PREFIX}/Admin/ReplaceWithAdditionalBuyers`, supplierInfo);
  }

  // --------------------- Supplier Management Methods End ----------------------

  // --------------------- Notification Management Methods Start ----------------------  //

  getNotificationsInfoByUserId(userId?: string): Observable<any> {
    return this.http.get<any>(`${API_PREFIX}/Notification/GetNotificationDetailsByUserId?userId=${userId}`);
  }

  saveNotification(addEditNotification: AddEditNotification): Observable<any> {
    return this.http.post<AddEditNotification>(`${API_PREFIX}/Notification/SaveNotification`, addEditNotification);
  }

  updateNotificationReadStatus(dismissNotification: NotificationFilters): Observable<any>{
    return this.http.post<NotificationFilters>(`${API_PREFIX}/Notification/UpdateNotificationReadStatus`, dismissNotification);
  }

  deleteNotification(deleteDetails: DeleteInfo): Observable<any>{
    return this.http.post<DeleteInfo>(`${API_PREFIX}/Notification/DeleteNotification`, deleteDetails);
  }

  // --------------------- Historical Supplier Quality Methods Start ----------------------  //

  getSupplierQualityData(supplierId?: string): Observable<QualityData> {
    return this.http.get<QualityData>(`${API_PREFIX}/Metrics/GetSupplierQualityData?supplierId=${supplierId}`);
  }

  getSupplierQualitySummaryTable(supplierId?: string, filters?: string): Observable<SupplierQualitySummaryTable> {
    return this.http.get<SupplierQualitySummaryTable>(`${API_PREFIX}/Metrics/GetSupplierQualitySummaryTable?supplierId=${supplierId}&filter=${filters}`);
  }

  historicalInfoExportData(exportData): Observable<any> {
    return this.http.post(`${API_PREFIX}/Metrics/DownloadSupplierQualityData`, exportData, { responseType: 'blob' });
  }
  

  // --------------------- Historical Supplier Quality Methods End ----------------------  //

  // --------------------- ADMIN All Home Page Conents Methods Start ----------------------  //

  getStaticContentForCompliance(data:string): Observable<HomePageContents> {
    return this.http.get<HomePageContents>(`${API_PREFIX}/StaticContent/GetAllHomePageStaticConents?sectionName=${data}`);
  }

getHomeSectionFile(fileName: string): Observable<Blob> {
    return this.http.get(`${API_PREFIX}/StaticContent/DownloadHomepageContentFile?fileName=${fileName}`, { responseType: 'blob' });
  }

getComplianceFile(fileName: string): Observable<Blob> {
    return this.http.get(`${API_PREFIX}/StaticContent/DownloadComplianceFile?fileName=${fileName}`, { responseType: 'blob' });
  }

  getAllHomePageStaticConents(data:string): Observable<HomePageContents> {
    return this.http.get<HomePageContents>(`${API_PREFIX}/Admin/GetAllHomePageStaticConents?sectionName=${data}`);
  }
  saveStaticContent(contentForm: HomePageContents): Observable<HomePageContents> {
    return this.http.post<HomePageContents>(`${API_PREFIX}/Admin/SaveStaticContent`, contentForm);
  }

  uploadStaticContent(uploadDocument): Observable<any> {
    return this.http.post<HomePageContents>(`${API_PREFIX}/Admin/UploadStaticContent`, uploadDocument);
  }
 

  uploadStaticFile(data: FormData) {
    return this.http.post(`${API_PREFIX}/Admin/UploadStaticContent`, data);
  }

  uploadPPAPContent(data: FormData) {
    return this.http.post(`${API_PREFIX}/WindchillData/UploadPPAPContent`, data);
  }

  removePPAPContentFile(fileName: string, ppapNum: string) {
    return this.http.delete(`${API_PREFIX}/WindchillData/RemovePPAPContentFile?fileName=${fileName}&PPAPNumber=${ppapNum}`);
  }

  uploadComplienceDocumentFile(data: FormData) {
    return this.http.post(`${API_PREFIX}/Admin/UploadComplienceDocument`, data);
  }

  removeUploadComplienceDocumentFile(sectionId:string, fileName:string) {
    return this.http.delete(`${API_PREFIX}/Admin/RemoveComplienceDocument?sectionId=${sectionId}&fileName=${fileName}`);
  }

  removeStaticContentFile(data: any) {
    const options = { headers: { 'Content-Type': 'application/json' }, body: data };
    return this.http.delete<any>(`${API_PREFIX}/Admin/RemoveStaticContentFile`, options);
  }

  editStaticContent(data) {
    return this.http.post(`${API_PREFIX}/Admin/EditStaticContent`, data);
  }

  getSolarFormConents(): Observable<AddEditSolarForm> {
    return this.http.get<AddEditSolarForm>(`${API_PREFIX}/Admin/GetAllSolarForms`);
  }
  saveSolarForm(contentForm: FormData): Observable<any> {
    return this.http.post(`${API_PREFIX}/Admin/SaveSolarForms`, contentForm);
  }

  updateSolarForm(contentForm: FormData): Observable<any> {
    return this.http.post(`${API_PREFIX}/Admin/UpdateSolarForms`, contentForm);
  }
  // --------------------- ADMIN All Home Page Conents Methods End ----------------------  //
  getDownloadStaticFile(documentslink: string): Observable<any> {
    return this.http.get(`${API_PREFIX}/Admin/DownloadStaticFile?filePath=${documentslink}`, { responseType: 'blob' });
  }

  //----------- PPAP START------//
  getPPAPReadOnlyDetails(data:string): Observable<any> {
    return this.http.get<any>(`${API_PREFIX}/WindchillData/GetPPAPReadOnlyDetails?pPAPNumber=${data}`);
  }

  submitPPAPForm(data): Observable<any> {
    return this.http.post(`${API_PREFIX}/WindchillData/UpdateSectionPPAPSubmittal`, data);
  }
  savePPAPForm(data): Observable<any> {
    return this.http.post(`${API_PREFIX}/WindchillData/UpdatePPADetails`, data);
  }
  //----------- PPAP END------//

  getSuppliersByUserId(cwsId: string): Observable<any> {
    return this.http.get<any>(`${API_PREFIX}/Supplier/GetSuppliersByUserId?userId=${cwsId}`);
  }
  
  getSuppliersByUserIdForMetrics(cwsId: string): Observable<any> {
    return this.http.get<any>(`${API_PREFIX}/Metrics/GetSuppliersByUserId?userId=${cwsId}`);
  }

  getWindchillSearch(textInfo: string, searchType: string): Observable<any> {
    return this.http.get<any>(`${API_PREFIX}/WindchillData/WindchillSearch?searchString=${textInfo}&searchType=${searchType}`);
  }

  searchWindchillRecord(textInfo: string, searchType: string): Observable<any> {
    return this.http.get(`${API_PREFIX}/WindchillData/WindchillSearch?searchString=${textInfo}&searchType=${searchType}`, { responseType: 'blob'});
  }

  getWindchillFileDownload(filePath: string): Observable<any> {
    return this.http.get(`${API_PREFIX}/WindchillData/WindChillFileDownload?filePath=${filePath}`, { responseType: 'blob' });
  }
  
  exportAuditData():Observable<Blob> {
    return this.http.get(`${API_PREFIX}/AuditService/DownloadFileAuditDetails`, { responseType: 'blob' });
  }
  exportMaterialData():Observable<Blob> {
    return this.http.get(`${API_PREFIX}/AuditService/SupplierFileDumpData`, { responseType: 'blob' });
  }
  exportSupplierReport():Observable<Blob> {
    return this.http.get(`${API_PREFIX}/AuditService/GetSuppliersData`, { responseType: 'blob' });
  }
  exportUserReport():Observable<Blob> {
    return this.http.get(`${API_PREFIX}/AuditService/GetUsersData`, { responseType: 'blob' });
  }

  getLogData(userAudit: UserAudit):Observable<any> {
    return this.http.post(`${API_PREFIX}/AuditService/AddAuditEntry`,  userAudit );
  }  

  setfileSize(fileSize: Number):Observable<Number> {
    return this.http.post<Number>(`${API_PREFIX}/Admin/UpdateFileSize?fileSize=${fileSize}`, fileSize);
  }

  getfileSize():Observable<Number> {
    return this.http.get<Number>(`${API_PREFIX}/Files/GetFileSize`);
  }

  getfileExtension():Observable<any> {
    return this.http.get<any>(`${API_PREFIX}/Admin/GetFileUploadConfiguration`);
  }

  updateFileExtension(updateAppConfig: AdditionalSettingsFileExtension):Observable<any> {
    return this.http.post<any>(`${API_PREFIX}/Admin/UpdateApplicationConfiguration`, updateAppConfig);
  }

  uploadRTAContent(data: FormData): Observable<any> {
    return this.http.post(`${API_PREFIX}/RTAForm/UploadRTAFile`, data, {responseType: 'text'});
  }
  // <-- RTA Config Starts here --> 
  saveRTAFormDetails(RTAFormInfo: RTADetails): Observable<any> {
    return this.http.post(`${API_PREFIX}/RTAForm/SaveRTAFormDetails`, RTAFormInfo);
  }

  updateRTAFormDetails(RTAFormInfo: RTADetails): Observable<any> {
    return this.http.post(`${API_PREFIX}/RTAForm/UpdateRTAFormDetails`, RTAFormInfo);
  }

  getRTAFormDetailsById(requestId: string): Observable<any> {
    return this.http.get<any>(`${API_PREFIX}/RTAForm/GetRTADetailByRTANumber?RTANumber=${requestId}`);
  }

  removeRTAFiles(removeRTAFile: RemoveRTAFiles): Observable<RemoveRTAFiles> {
    return this.http.post<RemoveRTAFiles>(`${API_PREFIX}/RTAForm/RemoveRTAFile`, removeRTAFile);
  }

  downloadQRImages(userCwsId: string, exportQR: string): Observable<DownloadQRImage> {
    return this.http.get<DownloadQRImage>(`${API_PREFIX}/QRCode/DownloadQRImage?userCWSID=${userCwsId}&partNumber=${exportQR}`);
  }

  searchQRImages(partNumber: string): Observable<any> {
    return this.http.get(`${QR_API_PREFIX}/WindChill/GetPartDetail?partNumber=${partNumber}`);
  }

  qrCodeScanMetrics(): Observable<QRCodeMetricsDTO> {
    return this.http.get<QRCodeMetricsDTO>(`${API_PREFIX}/QRCode/GetQRCodeMetrics`);
  }

  qrCodeScanMetricsExport(exportData: number): Observable<any> {
    return this.http.get(`${API_PREFIX}/QRCode/DownloadQRScanlog?noOfDays=${exportData}`, { responseType: 'blob' });
  }

  
}
